export type Rect = { top: number; left: number; width: number; height: number };

export type CalloutPlacement = {
  top: number;
  left: number;
  /** Which side of the target the callout sits on. */
  side: 'below' | 'above' | 'left' | 'right' | 'fallback';
};

const GAP = 12;
const VIEW_PAD = 12;
/** Keep clear of the Talk to Heidi dock + safe area. */
export const VOICE_DOCK_RESERVE_PX = 120;

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Coachmark placement: prefer below the target, then above, then sides.
 * Always clamps into the viewport and reserves space for the voice dock.
 */
export function placeVoiceCallout(
  target: Rect | null,
  callout: { width: number; height: number },
  viewport: { width: number; height: number },
  options?: { dockReserve?: number }
): CalloutPlacement {
  const dockReserve = options?.dockReserve ?? VOICE_DOCK_RESERVE_PX;
  const maxTop = Math.max(VIEW_PAD, viewport.height - dockReserve - callout.height);
  const maxLeft = Math.max(VIEW_PAD, viewport.width - VIEW_PAD - callout.width);

  if (!target) {
    return {
      top: clamp(viewport.height - dockReserve - callout.height - GAP, VIEW_PAD, maxTop),
      left: clamp((viewport.width - callout.width) / 2, VIEW_PAD, maxLeft),
      side: 'fallback',
    };
  }

  const centerX = target.left + target.width / 2;
  const preferLeft = clamp(centerX - callout.width / 2, VIEW_PAD, maxLeft);

  const belowTop = target.top + target.height + GAP;
  if (belowTop + callout.height <= viewport.height - dockReserve) {
    return { top: belowTop, left: preferLeft, side: 'below' };
  }

  const aboveTop = target.top - GAP - callout.height;
  if (aboveTop >= VIEW_PAD) {
    return {
      top: clamp(aboveTop, VIEW_PAD, maxTop),
      left: preferLeft,
      side: 'above',
    };
  }

  const rightLeft = target.left + target.width + GAP;
  if (rightLeft + callout.width <= viewport.width - VIEW_PAD) {
    return {
      top: clamp(target.top, VIEW_PAD, maxTop),
      left: rightLeft,
      side: 'right',
    };
  }

  const leftLeft = target.left - GAP - callout.width;
  if (leftLeft >= VIEW_PAD) {
    return {
      top: clamp(target.top, VIEW_PAD, maxTop),
      left: leftLeft,
      side: 'left',
    };
  }

  return {
    top: clamp(belowTop, VIEW_PAD, maxTop),
    left: preferLeft,
    side: 'fallback',
  };
}
